import { redirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { POST_LOGIN_REDIRECT_PATH } from "@/lib/trade-url";

const ACCESS_TOKEN_COOKIE = "access_token";
const REFRESH_TOKEN_COOKIE = "refresh_token";

const crawlerRe =
  /facebookexternalhit|twitterbot|slackbot|discordbot|linkedinbot|whatsapp|telegrambot|pinterest|embedly|quora|vkshare|applebot|googlebot|bingbot/;

export const requestGuard = createMiddleware().server(
  async ({ request, next }) => {
    const url = new URL(request.url);
    const { pathname, searchParams } = url;
    const method = request.method;
    const ua = (request.headers.get("user-agent") || "").toLowerCase();

    // 1) /maintenance → /explore
    if (pathname === "/maintenance") {
      // return Response.redirect(new URL('/explore', url), 307)
      throw redirect({ to: "/login", statusCode: 307 });
    }

    // 2) Crawl-shim: for trade/*, reroute crawlers to head-only route
    //    Keep your main /trade page CSR-only.
    if ((method === "GET" || method === "HEAD") && crawlerRe.test(ua)) {
      // Match /trade/:chain/:address (adjust if your pattern differs)
      const tradeMatch = pathname.match(/^\/trade\/([^/]+)\/([^/]+)$/);
      if (tradeMatch) {
        return next();
      }
    }

    // 3) Auth check via cookies
    const cookieHeader = request.headers.get("cookie") || "";
    const isAuthenticated =
      cookieHeader.includes(`${ACCESS_TOKEN_COOKIE}=`) ||
      cookieHeader.includes(`${REFRESH_TOKEN_COOKIE}=`);

    // 4) Referral path /p/:code → login (or /explore if already authed)
    if (pathname.startsWith("/p/")) {
      const referralCode = pathname.split("/")[2];
      if (!isAuthenticated) {
        const to = new URL("/login", url);
        if (referralCode) to.searchParams.set("ref", referralCode);
        throw redirect({
          to: "/login",
          search: { ref: referralCode },
          statusCode: 307,
        });
      }
      throw redirect({ to: POST_LOGIN_REDIRECT_PATH, statusCode: 307 });
    }

    // 5) Public paths
    const publicPaths = new Set<string>([
      "/login",
      "/p",
      "/share",
      "/auth/login/google",
      "/auth/login/discord",
    ]);
    const isPublicPath =
      [...publicPaths].some(
        (p) => pathname === p || pathname.startsWith(p + "/"),
      ) ||
      // Always allow OG/crawler routes and static assets
      pathname.startsWith("/og/") ||
      pathname.startsWith("/images/") ||
      pathname.startsWith("/logos/") ||
      pathname === "/favicon.ico" ||
      pathname.startsWith("/static/");

    // 6) Protect all but public routes
    if (!isPublicPath && !isAuthenticated) {
      const ref = searchParams.get("ref");
      throw redirect({
        to: "/login",
        search: { ref: ref ?? undefined },
        statusCode: 307,
      });
    }

    // 7) Logged-in users hitting /login → default trading landing
    if (pathname === "/login" && isAuthenticated) {
      throw redirect({ to: POST_LOGIN_REDIRECT_PATH, statusCode: 307 });
    }

    // 8) / → default trading landing
    if (pathname === "/") {
      throw redirect({ to: POST_LOGIN_REDIRECT_PATH, statusCode: 307 });
    }

    // Fall through
    return next();
  },
);
